import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { useContext, useEffect, useMemo } from "react";
import { InputOrderData } from "@shared/features/trades/common/types";
import { getWeightedAverageEntryPrice } from "@shared/features/trades/common/utils";
import { getDefaultOrderBasePrice } from "@shared/features/bots/BotForm/common/utils";
import { DEFAULT_STOP } from "@shared/features/bots/BotForm/common/constants";
import { OrderBaseline, OrderTypes } from "@shared/types";
import {
  buildOrdersFromFreeTextData,
  getOrdersWithExisting,
  getOrdersWithUpdatedDistance,
  getParsedEntries,
  updateOrdersWithAmount,
} from "@shared/features/tradingTerminal/common/utils";
import {
  GeneralConfigurationData,
  TradeTypes,
} from "@shared/features/tradingTerminal/common/types";

const useSharedOrders = (
  generalConfigurationData: Record<OrderTypes, GeneralConfigurationData>
) => {
  const {
    entryOrders,
    takeProfitOrders,
    stopOrders,
    setEntryOrders,
    setTakeProfitOrders,
    setStopOrders,
    useTakeProfitOrders,
    setUseTakeProfitOrders,
    useStopOrders,
    setUseStopOrders,
    defaultStop,
    freeTextCreateTradeLoading,
    setUpdatedItemsToFreeTextData,
    amount,
    freeTextTradeData,
    getCreateGeneralDataQuery,
    quantity,
    quantityAmount,
    symbols,
    precisionConfig,
    freeTextProcessed,
    setFreeTextProcessed,
    useFreeTextData,
    account,
    exchanges,
    currentPrice,
    accountChanged,
    setAccountChanged,
    ordersErrors,
    setExistingQuantityOrder,
    isSignal,
  } = useContext(TradingTerminalContext)!;

  const entriesData = generalConfigurationData[OrderTypes.ENTRY];
  const takeProfitsData = generalConfigurationData[OrderTypes.TAKE_PROFIT];
  const symbol = symbols[0];
  const loading = getCreateGeneralDataQuery.isFetching;

  const existingQuantityOrder: InputOrderData | undefined = useMemo(
    () =>
      parseFloat(quantity || "0")
        ? {
            price: (currentPrice || 0).toString(),
            percent: "100",
            amount: quantityAmount.toString(),
          }
        : undefined,
    [quantity, currentPrice, quantityAmount]
  );

  useEffect(() => {
    setExistingQuantityOrder(existingQuantityOrder);
  }, [existingQuantityOrder]);

  const ordersForWeightedAverage = useMemo(() => {
    return getOrdersWithExisting(entryOrders, existingQuantityOrder);
  }, [entryOrders, existingQuantityOrder]);
  const weightedAverageEntryPrice = useMemo(
    () => getWeightedAverageEntryPrice(ordersForWeightedAverage),
    [ordersForWeightedAverage]
  );
  const stopBaseEntryPrice = useMemo(
    () =>
      getDefaultOrderBasePrice(
        (defaultStop || DEFAULT_STOP).baseline as OrderBaseline,
        getParsedEntries(entryOrders)
      ),
    [defaultStop, entryOrders]
  );

  const getBaselineEntryPrice = (orderType: OrderTypes) => {
    return orderType === OrderTypes.STOP
      ? stopBaseEntryPrice
      : weightedAverageEntryPrice;
  };

  useEffect(() => {
    if (
      !loading &&
      !freeTextCreateTradeLoading &&
      useFreeTextData &&
      freeTextTradeData &&
      !freeTextProcessed &&
      symbol === freeTextTradeData?.symbol &&
      entriesData.strategy &&
      takeProfitsData.strategy
    ) {
      const {
        [OrderTypes.ENTRY]: freeTextEntryOrders,
        [OrderTypes.TAKE_PROFIT]: freeTextTakeProfitOrders,
        [OrderTypes.STOP]: freeTextStopOrders,
      } = buildOrdersFromFreeTextData(
        freeTextTradeData,
        Number(amount) || 0,
        entriesData,
        takeProfitsData,
        getBaselineEntryPrice,
        !!freeTextTradeData?.reverse,
        freeTextTradeData?.trade_type === TradeTypes.BREAKOUT,
        precisionConfig,
        isSignal
      );
      setEntryOrders(freeTextEntryOrders);
      setUpdatedItemsToFreeTextData(OrderTypes.ENTRY, false);

      if (freeTextTakeProfitOrders?.length) {
        setTakeProfitOrders(freeTextTakeProfitOrders);
        setUseTakeProfitOrders(true);
        setUpdatedItemsToFreeTextData(OrderTypes.TAKE_PROFIT, false);
      } else {
        setUseTakeProfitOrders(false);
      }

      if (freeTextStopOrders?.length) {
        setStopOrders(freeTextStopOrders);
        setUseStopOrders(true);
        setUpdatedItemsToFreeTextData(OrderTypes.STOP, false);
      } else {
        setUseStopOrders(false);
      }
      setFreeTextProcessed(true);
      if (accountChanged) setAccountChanged(false);
    }
  }, [
    loading,
    freeTextCreateTradeLoading,
    useFreeTextData,
    freeTextTradeData,
    freeTextProcessed,
    symbol,
    account?.client_id,
    exchanges,
    takeProfitsData.strategy,
    entriesData.strategy,
    isSignal,
  ]);

  useEffect(() => {
    if (!isSignal) {
      setEntryOrders((prevEntryOrders) =>
        updateOrdersWithAmount(
          prevEntryOrders,
          Number(amount) || 0,
          precisionConfig
        )
      );
    }
  }, [amount, isSignal]);

  useEffect(() => {
    setTakeProfitOrders((prevOrders) =>
      getOrdersWithUpdatedDistance(
        prevOrders,
        weightedAverageEntryPrice,
        precisionConfig?.percent
      )
    );
  }, [weightedAverageEntryPrice]);

  useEffect(() => {
    setStopOrders((prevOrders) =>
      getOrdersWithUpdatedDistance(
        prevOrders,
        stopBaseEntryPrice,
        precisionConfig?.percent
      )
    );
  }, [stopBaseEntryPrice]);

  const orders = useMemo(() => {
    return {
      [OrderTypes.ENTRY]: entryOrders,
      [OrderTypes.TAKE_PROFIT]: takeProfitOrders,
      [OrderTypes.STOP]: stopOrders,
    };
  }, [entryOrders, takeProfitOrders, stopOrders]);

  const setOrders = {
    [OrderTypes.ENTRY]: setEntryOrders,
    [OrderTypes.TAKE_PROFIT]: setTakeProfitOrders,
    [OrderTypes.STOP]: setStopOrders,
  };

  const useOrdersByType = {
    [OrderTypes.ENTRY]: true,
    [OrderTypes.TAKE_PROFIT]: useTakeProfitOrders,
    [OrderTypes.STOP]: useStopOrders,
  };

  const setUseOrdersByType = {
    [OrderTypes.ENTRY]: undefined,
    [OrderTypes.TAKE_PROFIT]: setUseTakeProfitOrders,
    [OrderTypes.STOP]: setUseStopOrders,
  };

  return {
    orders,
    setOrders,
    useOrdersByType,
    setUseOrdersByType,
    getBaselineEntryPrice,
    ordersErrors,
  };
};

export default useSharedOrders;
